import React, { useState, useEffect } from 'react';
import { getWhyUsItems, createWhyUs, updateWhyUs, deleteWhyUs, toggleWhyUsStatus, getCategories } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import WhyUsForm from '../forms/WhyUsForm';
import toast from 'react-hot-toast';

const WhyUsModule = () => {
  const [whyUsItems, setWhyUsItems] = useState([]);
  const [whyUsCategories, setWhyUsCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const columns = ['Image', 'Title', 'Description', 'Points', 'Status', 'Actions'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [whyUsResponse, categoriesResponse] = await Promise.all([
        getWhyUsItems(),
        getCategories()
      ]);
      
      let whyUsData = whyUsResponse.data || whyUsResponse || [];
      
      // Ensure all items have a categoryName for display
      whyUsData = whyUsData.map(item => ({
        ...item,
        categoryName: item.categoryName || 'General',
        displayCategory: item.categoryName || item.category?.name || 'General'
      }));
      
      setWhyUsItems(whyUsData);
      
      let categoriesData = [];
      if (categoriesResponse && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        categoriesData = categoriesResponse.data.filter(cat => cat.type === 'whyus');
      } else if (categoriesResponse && Array.isArray(categoriesResponse)) {
        categoriesData = categoriesResponse.filter(cat => cat.type === 'whyus');
      }
      
      setWhyUsCategories(categoriesData);
      
      // Set default category filter to 'all' instead of first category
      setSelectedCategory('all');
      
      console.log('Why Us items loaded:', whyUsData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching why us items');
      setWhyUsItems([]);
      setWhyUsCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteWhyUs(id);
        toast.success('Why Us item deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Error deleting why us item');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleWhyUsStatus(id);
      toast.success(`Why Us item ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Error toggling why us item status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getPointsPreview = (points) => {
    if (!points || points.length === 0) return '-';
    return points.length > 2 ? `${points.slice(0, 2).join(', ')}...` : points.join(', ');
  };

  // Updated filtering logic - show all items by default
  const filteredItems = selectedCategory === 'all' || !selectedCategory
    ? whyUsItems
    : whyUsItems.filter(item => 
        item.categoryName === selectedCategory || 
        item.category === selectedCategory ||
        item.displayCategory === selectedCategory
      );

  if (loading) {
    return <LoadingSpinner message="Loading why us items..." />;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Why Us Management</h2>
        <p style={{ color: '#5A6E7F' }}>Manage reasons why clients choose us</p>
      </div>

      {/* Category Filter Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: '#F7F9FC',
        borderRadius: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, color: '#0A192F' }}>
            <i className="fas fa-filter"></i> Filter by Category:
          </span>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              background: 'white',
              fontSize: '0.875rem'
            }}
          >
            <option value="all">-- All Categories --</option>
            {whyUsCategories.map((cat) => (
              <option key={cat._id} value={cat.name}>
                {cat.icon && <span style={{ marginRight: '0.5rem' }}>{cat.icon}</span>}
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              padding: '0.5rem 1rem',
              background: viewMode === 'grid' ? '#2C7A7B' : '#FFFFFF',
              color: viewMode === 'grid' ? 'white' : '#5A6E7F',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <i className="fas fa-th-large"></i> Grid View
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '0.5rem 1rem',
              background: viewMode === 'table' ? '#2C7A7B' : '#FFFFFF',
              color: viewMode === 'table' ? 'white' : '#5A6E7F',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <i className="fas fa-table"></i> Table View
          </button>
        </div>
      </div>

      {/* Add Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ 
            padding: '0.6rem 1.2rem', 
            background: '#2C7A7B', 
            color: 'white', 
            border: 'none', 
            borderRadius: '10px', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontWeight: 500
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
        >
          <i className="fas fa-plus"></i> Add New Why Us Item
        </button>
      </div>

      {/* Stats */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ color: '#5A6E7F', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-star" style={{ color: '#2C7A7B' }}></i>
          <strong>{filteredItems.length}</strong> {filteredItems.length === 1 ? 'item' : 'items'} found
          {selectedCategory && selectedCategory !== 'all' && <span> in <strong>{selectedCategory}</strong></span>}
        </p>
      </div>

      {filteredItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '20px', border: '2px dashed #E2E8F0' }}>
          <i className="fas fa-star" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }} />
          <p style={{ color: '#5A6E7F', marginBottom: '1rem', fontSize: '1.1rem' }}>No why us items found</p>
          <button onClick={() => { setEditingItem(null); setShowModal(true); }} style={{ padding: '0.6rem 1.2rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 500 }}>
            <i className="fas fa-plus"></i> Create Your First Why Us Item
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredItems.map((item) => (
            <WhyUsCard
              key={item._id}
              item={item}
              getFullImageUrl={getFullImageUrl}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem' }}>
                    {item.imageUrl ? (
                      <img src={getFullImageUrl(item.imageUrl)} alt={item.title} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', background: '#F7F9FC', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className={item.icon || 'fas fa-star'} style={{ color: '#CBD5E1' }} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>
                    <div>{item.title}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                      <i className={item.icon || 'fas fa-star'} style={{ marginRight: '0.25rem' }} /> {item.icon}
                    </div>
                  </td>
                  <td style={{ padding: '1rem', maxWidth: '250px' }}>
                    <div style={{ fontSize: '0.85rem', color: '#5A6E7F' }}>
                      {item.description && item.description.length > 100 ? `${item.description.substring(0, 100)}...` : item.description}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ background: '#E3F2FD', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem' }}>
                      {item.points?.length || 0} points
                    </span>
                    <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                      {getPointsPreview(item.points)}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleToggle(item._id, item.isActive)} 
                      style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: item.isActive ? '#D4EDDA' : '#F8D7DA', color: item.isActive ? '#155724' : '#721C24', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      {item.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleEdit(item)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}
                    >
                      <i className="fas fa-edit" /> Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(item._id)} 
                      style={{ padding: '0.25rem 0.75rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      <i className="fas fa-trash" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <WhyUsForm 
          editingItem={editingItem} 
          onClose={handleCloseModal} 
          onRefresh={fetchData}
          whyUsCategories={whyUsCategories}
        />
      )}
    </div>
  );
};

// Why Us Card Component
const WhyUsCard = ({ item, getFullImageUrl, onEdit, onDelete, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        border: '1px solid #E2E8F0',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Status Badge */}
      <div style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 1 }}>
        <button
          onClick={() => onToggle(item._id, item.isActive)}
          style={{
            padding: '0.3rem 0.8rem',
            borderRadius: '20px',
            border: 'none',
            background: item.isActive ? '#10B981' : '#EF4444',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.7rem',
            fontWeight: 600
          }}
        >
          {item.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      <div style={{ padding: '1.5rem', textAlign: 'center' }}>
        {/* Icon/Image */}
        <div style={{
          width: '70px',
          height: '70px',
          background: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1rem'
        }}>
          {item.imageUrl ? (
            <img 
              src={getFullImageUrl(item.imageUrl)} 
              alt={item.title} 
              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }} 
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `<i class="${item.icon || 'fas fa-star'}" style="font-size: 2rem; color: white;"></i>`;
              }}
            />
          ) : (
            <i className={item.icon || 'fas fa-star'} style={{ fontSize: '2rem', color: 'white' }}></i>
          )}
        </div>

        <h3 style={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: '#0A192F',
          marginBottom: '0.5rem'
        }}>
          {item.title}
        </h3>

        {item.description && (
          <p style={{
            fontSize: '0.85rem',
            color: '#5A6E7F',
            lineHeight: '1.5',
            marginBottom: '1rem'
          }}>
            {item.description.length > 100 ? `${item.description.substring(0, 100)}...` : item.description}
          </p>
        )}

        {item.points && item.points.length > 0 && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            {item.points.slice(0, 3).map((point, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#5A6E7F' }}>
                <i className="fas fa-check-circle" style={{ color: '#10B981', fontSize: '0.7rem' }}></i>
                <span>{point.length > 50 ? `${point.substring(0, 50)}...` : point}</span>
              </div>
            ))}
            {item.points.length > 3 && (
              <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                +{item.points.length - 3} more points
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <button
            onClick={() => onEdit(item)}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: '#2C7A7B',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
          >
            <i className="fas fa-edit"></i> Edit
          </button>
          <button
            onClick={() => onDelete(item._id)}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: '#DC2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#DC2626'}
          >
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhyUsModule;